import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Slot group definitions — photos
// ============================================================================

const photo1Def: SlotGroupDefinition = {
  label: 'Photo – Intro (right)',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/3df7b7e3/dms3rep/multi/opt/61dc756b42ccb8.10531271353-1920w.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Modern lounge with wooden ceiling, bar stools, tables, and a gray counter in a bright open interior',
    },
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Photo – Wide (left)',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/3df7b7e3/dms3rep/multi/opt/61dc75c8c787a8.47699599278-1920w.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Modern open-plan living room with wood ceiling, pink chairs, and a long wooden dining table',
    },
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Photo – Narrow (right)',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/3df7b7e3/dms3rep/multi/opt/pexels-photo-3184360-1920w.jpeg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Team meeting around laptops at a wooden table in a bright office, with a corkboard on the wall',
    },
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Photo – Pair (left)',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/3df7b7e3/dms3rep/multi/opt/61dc756ac73843.52664627965-1920w.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Modern lounge with foosball table, wall-mounted TV, bean bag seating, and wood-slat ceiling',
    },
  },
};

const photo5Def: SlotGroupDefinition = {
  label: 'Photo – Pair (right)',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/3df7b7e3/dms3rep/multi/opt/61dc756b694ec2.61882351493-1920w.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Modern lounge with pink chairs, round coffee table, and open seating area with warm lighting',
    },
  },
};

const photo6Def: SlotGroupDefinition = {
  label: 'Photo – Trade Up',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://irp.cdn-website.com/3df7b7e3/dms3rep/multi/opt/61dc75c8e84b11.16870632192-1920w.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Modern conference room with wooden table, white chairs, wall screen, and large window.',
    },
  },
};

// ============================================================================
// Slot group definitions — pricing plans
// ============================================================================

const plan1Def: SlotGroupDefinition = {
  label: 'Plan 1',
  fields: {
    label_line_1: { label: 'Label Line 1', type: 'text', defaultValue: 'Full-time' },
    label_line_2: { label: 'Label Line 2', type: 'text', defaultValue: 'Monthly' },
    price: { label: 'Price', type: 'text', defaultValue: '$299' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Unlimited use of workspace & lounge + 10 hours of conference room access',
    },
  },
};

const plan2Def: SlotGroupDefinition = {
  label: 'Plan 2',
  fields: {
    label_line_1: { label: 'Label Line 1', type: 'text', defaultValue: 'Part-time' },
    label_line_2: { label: 'Label Line 2', type: 'text', defaultValue: 'Monthly' },
    price: { label: 'Price', type: 'text', defaultValue: '$150' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue:
        '10 days with full use of workspace & lounge + 5 hours of conference room access',
    },
  },
};

const plan3Def: SlotGroupDefinition = {
  label: 'Plan 3',
  fields: {
    label_line_1: { label: 'Label Line 1', type: 'text', defaultValue: 'Conference' },
    label_line_2: { label: 'Label Line 2', type: 'text', defaultValue: 'Room Rental' },
    price: { label: 'Price', type: 'text', defaultValue: '$50/hr' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Reserve a private conference space with large screen',
    },
  },
};

const plan4Def: SlotGroupDefinition = {
  label: 'Plan 4',
  fields: {
    label_line_1: { label: 'Label Line 1', type: 'text', defaultValue: 'Daily' },
    label_line_2: { label: 'Label Line 2', type: 'text', defaultValue: 'Rate' },
    price: { label: 'Price', type: 'text', defaultValue: '$25/day' },
    description: {
      label: 'Description',
      type: 'text',
      defaultValue: '1 day use of workspace & lounge',
    },
  },
};

// ============================================================================
// Slot group definitions — CTA
// ============================================================================

const emailCtaDef: SlotGroupDefinition = {
  label: 'Email CTA Button',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'mailto:p_723000@dolben.lead-mgmt.com',
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Email Us to Reserve Your Space Today',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers
// ============================================================================

const BTN_CLS =
  'inline-flex items-center justify-center gap-3 px-8 py-4 font-body text-sm font-medium uppercase tracking-[0.18em] text-center' +
  ' bg-transparent text-black border border-black transition-colors duration-300' +
  ' hover:bg-secondary hover:border-secondary hover:text-white' +
  ' focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary';

const RICH_P_RESET = '[&_p]:m-0';

function EnvelopeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="1.5" />
      <path d="M3.5 6l8.5 7 8.5-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlanCard({ plan }: { plan: Record<string, string | null> }) {
  return (
    <div className="flex h-full flex-col items-center bg-[#737373] px-5 py-8 text-center text-white">
      <p className="m-0 font-heading text-lg font-bold uppercase leading-tight tracking-wide sm:text-xl">
        {plan.label_line_1}
        {plan.label_line_2 ? (
          <>
            <br />
            {plan.label_line_2}
          </>
        ) : null}
      </p>
      <p className="m-0 mt-4 font-heading text-3xl font-extrabold uppercase leading-none sm:text-4xl lg:text-5xl">
        {plan.price}
      </p>
      <p className="m-0 mt-4 font-body text-sm leading-relaxed sm:text-base">{plan.description}</p>
    </div>
  );
}

const PHOTO_IMG_CLS = 'h-full w-full object-cover';

// ============================================================================
// Component
// ============================================================================

export function TradersWorksContent() {
  const photo1 = useSlotGroup('tradersworkscontent.photo_1', photo1Def);
  const photo2 = useSlotGroup('tradersworkscontent.photo_2', photo2Def);
  const photo3 = useSlotGroup('tradersworkscontent.photo_3', photo3Def);
  const photo4 = useSlotGroup('tradersworkscontent.photo_4', photo4Def);
  const photo5 = useSlotGroup('tradersworkscontent.photo_5', photo5Def);
  const photo6 = useSlotGroup('tradersworkscontent.photo_6', photo6Def);

  const plan1 = useSlotGroup('tradersworkscontent.plan_1', plan1Def);
  const plan2 = useSlotGroup('tradersworkscontent.plan_2', plan2Def);
  const plan3 = useSlotGroup('tradersworkscontent.plan_3', plan3Def);
  const plan4 = useSlotGroup('tradersworkscontent.plan_4', plan4Def);

  const emailCta = useSlotGroup('tradersworkscontent.email_cta', emailCtaDef);
  const email = useContentToken('tradersworkscontent.email');

  return (
    <section className="bg-[var(--vibe-color-background)] py-12 md:py-16">
      <div className="mx-auto max-w-7xl space-y-4 px-4 sm:px-6 md:space-y-6 lg:px-8">
        {/* Intro: Traders / Works heading + photo */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
          <div className="flex flex-col justify-center md:col-span-2">
            <RichTextContent
              path="tradersworkscontent.heading"
              as="h2"
              className={`font-heading text-[clamp(2rem,1rem+5vw,6rem)] font-extrabold uppercase leading-none tracking-wide text-[#63666a] ${RICH_P_RESET}`}
            />
            <RichTextContent
              path="tradersworkscontent.subheading"
              as="h3"
              className={`mt-1 font-heading text-[clamp(1.25rem,0.6rem+3vw,3.75rem)] font-light uppercase leading-none tracking-[0.35em] text-[#63666a] ${RICH_P_RESET}`}
            />
            <RichTextContent
              path="tradersworkscontent.tagline"
              as="h3"
              className={`mt-4 font-body text-base font-semibold uppercase tracking-[0.2em] text-[var(--vibe-color-text)] sm:text-xl ${RICH_P_RESET}`}
            />
          </div>
          <EditableSlotGroup
            groupPath="tradersworkscontent.photo_1"
            definition={photo1Def}
            className="aspect-[4/5] overflow-hidden md:aspect-square"
          >
            <VibesitesImage
              url={photo1.image || ''}
              alt={photo1.alt || ''}
              sizes="(min-width: 768px) 33vw, 50vw"
              className={PHOTO_IMG_CLS}
            />
          </EditableSlotGroup>
        </div>

        {/* Wide + narrow photo row */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
          <EditableSlotGroup
            groupPath="tradersworkscontent.photo_2"
            definition={photo2Def}
            className="aspect-[4/5] overflow-hidden md:col-span-2 md:aspect-[2/1]"
          >
            <VibesitesImage
              url={photo2.image || ''}
              alt={photo2.alt || ''}
              sizes="(min-width: 768px) 66vw, 50vw"
              className={PHOTO_IMG_CLS}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="tradersworkscontent.photo_3"
            definition={photo3Def}
            className="aspect-[4/5] overflow-hidden md:aspect-auto"
          >
            <VibesitesImage
              url={photo3.image || ''}
              alt={photo3.alt || ''}
              sizes="(min-width: 768px) 33vw, 50vw"
              className={PHOTO_IMG_CLS}
            />
          </EditableSlotGroup>
        </div>

        {/* Lifestyle heading + feature list */}
        <div className="py-6 md:py-10">
          <RichTextContent
            path="tradersworkscontent.features_heading"
            as="h2"
            className={`font-heading text-base font-bold uppercase text-[#494949] sm:text-2xl lg:text-3xl ${RICH_P_RESET}`}
          />
          <RichTextContent
            path="tradersworkscontent.features_list"
            as="div"
            className={
              'mt-5 font-body text-base leading-relaxed text-[#494949] sm:text-lg lg:text-xl' +
              ' [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_p]:m-0' +
              ' [&_strong]:font-bold [&_em]:not-italic [&_em]:font-bold [&_em]:text-secondary'
            }
          />
        </div>

        {/* Pricing plans */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
          <EditableSlotGroup groupPath="tradersworkscontent.plan_1" definition={plan1Def} className="h-full">
            <PlanCard plan={plan1} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="tradersworkscontent.plan_2" definition={plan2Def} className="h-full">
            <PlanCard plan={plan2} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="tradersworkscontent.plan_3" definition={plan3Def} className="h-full">
            <PlanCard plan={plan3} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="tradersworkscontent.plan_4" definition={plan4Def} className="h-full">
            <PlanCard plan={plan4} />
          </EditableSlotGroup>
        </div>

        {/* Photo pair */}
        <div className="grid grid-cols-2 gap-4 md:gap-6">
          <EditableSlotGroup
            groupPath="tradersworkscontent.photo_4"
            definition={photo4Def}
            className="aspect-[4/3] overflow-hidden"
          >
            <VibesitesImage url={photo4.image || ''} alt={photo4.alt || ''} sizes="50vw" className={PHOTO_IMG_CLS} />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="tradersworkscontent.photo_5"
            definition={photo5Def}
            className="aspect-[4/3] overflow-hidden"
          >
            <VibesitesImage url={photo5.image || ''} alt={photo5.alt || ''} sizes="50vw" className={PHOTO_IMG_CLS} />
          </EditableSlotGroup>
        </div>

        {/* Trade up */}
        <div className="grid grid-cols-2 items-center gap-4 md:gap-6">
          <EditableSlotGroup
            groupPath="tradersworkscontent.photo_6"
            definition={photo6Def}
            className="aspect-[4/3] overflow-hidden"
          >
            <VibesitesImage url={photo6.image || ''} alt={photo6.alt || ''} sizes="50vw" className={PHOTO_IMG_CLS} />
          </EditableSlotGroup>
          <div className="px-2 text-center md:px-6">
            <RichTextContent
              path="tradersworkscontent.tradeup_title"
              as="h2"
              className={`font-heading text-[clamp(1.5rem,0.5rem+4.5vw,4.5rem)] font-bold uppercase leading-none text-primary ${RICH_P_RESET}`}
            />
            <RichTextContent
              path="tradersworkscontent.tradeup_subtitle"
              as="p"
              className={`mt-2 font-heading text-[clamp(1.25rem,0.4rem+3.5vw,3.4rem)] font-bold uppercase leading-tight text-[#63666a] ${RICH_P_RESET}`}
            />
            <RichTextContent
              path="tradersworkscontent.tradeup_cta_text"
              as="p"
              className={`mt-3 font-heading text-lg font-semibold uppercase text-secondary sm:text-2xl lg:text-3xl ${RICH_P_RESET}`}
            />
          </div>
        </div>

        {/* Email CTA */}
        <div className="flex flex-col items-center gap-4 pt-8 text-center md:pt-12">
          <EditableSlotGroup groupPath="tradersworkscontent.email_cta" definition={emailCtaDef}>
            <a
              href={emailCta.url || '#'}
              target={emailCta._target || '_self'}
              rel={emailCta._target === '_blank' ? 'noopener noreferrer' : undefined}
              className={BTN_CLS}
            >
              <EnvelopeIcon className="h-5 w-5 shrink-0" />
              <span>{emailCta.label || 'Email Us to Reserve Your Space Today'}</span>
            </a>
          </EditableSlotGroup>
          <EditableToken path="tradersworkscontent.email" as="p" className="m-0 font-body text-lg sm:text-2xl">
            <a
              href={`mailto:${email || ''}`}
              className="text-[var(--vibe-color-text)] underline-offset-4 transition-colors hover:text-secondary hover:underline"
            >
              {email}
            </a>
          </EditableToken>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TradersWorksContent',
  displayName: 'Traders Works Content',
  tokens: {
    'tradersworkscontent.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Traders</p>',
    },
    'tradersworkscontent.subheading': {
      label: 'Subheading',
      type: 'richtext',
      defaultValue: '<p>Works</p>',
    },
    'tradersworkscontent.tagline': {
      label: 'Tagline',
      type: 'richtext',
      defaultValue: '<p>Work &amp; Play Spaces</p>',
    },
    'tradersworkscontent.features_heading': {
      label: 'Features Heading',
      type: 'richtext',
      defaultValue: '<p>Lifestyle-oriented work spaces with a casual atmosphere</p>',
    },
    'tradersworkscontent.features_list': {
      label: 'Features List',
      type: 'richtext',
      defaultValue:
        '<ul><li><strong>Open Monday - Friday, 9:00am - 6:00pm</strong></li><li><em>WORK</em>: a selection of comfortable co-working areas and conference spaces</li><li><em>PLAY</em>: Need a break? Enjoy the catering kitchen, game room and lounge, or run a quick errand at the shopping area nextdoor</li></ul>',
    },
    'tradersworkscontent.tradeup_title': {
      label: 'Trade Up Title',
      type: 'richtext',
      defaultValue: '<p>Trade Up</p>',
    },
    'tradersworkscontent.tradeup_subtitle': {
      label: 'Trade Up Subtitle',
      type: 'richtext',
      defaultValue: '<p>Your workspace</p>',
    },
    'tradersworkscontent.tradeup_cta_text': {
      label: 'Trade Up CTA Text',
      type: 'richtext',
      defaultValue: '<p>Contact us today!</p>',
    },
    'tradersworkscontent.email': {
      label: 'Email Address',
      type: 'email',
      defaultValue: 'tradersrow@dolben.com',
    },
  },
  slotGroups: {
    'tradersworkscontent.photo_1': photo1Def,
    'tradersworkscontent.photo_2': photo2Def,
    'tradersworkscontent.photo_3': photo3Def,
    'tradersworkscontent.photo_4': photo4Def,
    'tradersworkscontent.photo_5': photo5Def,
    'tradersworkscontent.photo_6': photo6Def,
    'tradersworkscontent.plan_1': plan1Def,
    'tradersworkscontent.plan_2': plan2Def,
    'tradersworkscontent.plan_3': plan3Def,
    'tradersworkscontent.plan_4': plan4Def,
    'tradersworkscontent.email_cta': emailCtaDef,
  },
};

(TradersWorksContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default TradersWorksContent;
